import { NotificationController } from 'core-components/library/popup'
import { getCloudinaryUrl } from 'src/core/classes/helpers/CloudinaryHelper'
import { getActiveTokens } from 'src/core/classes/helpers/StorageHelper'
import { proxy } from 'src/core/classes/helpers/UrlHelper'
import {
  IBaseFileData,
  IReceiveModels,
  ISaveFileData,
  ISaveResponse,
  IShortFilesUploadData,
  TFilesUploadHandler,
} from 'src/core/classes/models/IFileData'
import { IUploadAdapter } from 'src/core/classes/models/IUploadAdapter'
import {
  ATTACHMENT_FORM_CODE,
  ATTACHMENT_FORM_ID,
  FILE_FIELD_NAME,
} from 'src/core/constants/attachments'

const filesUploadHandler: TFilesUploadHandler = async ({
  preparedModels,
  models,
  url,
  fieldName,
  method,
}) => {
  if (!preparedModels.length) {
    const emptyResponseBody = JSON.stringify({})
    return new Response(emptyResponseBody)
  }
  const modelsJSON = JSON.stringify(preparedModels)
  const formData = new FormData()
  formData.append('models', modelsJSON)
  models.forEach(({ file }, index) => {
    formData.append(`${fieldName}[${index}]`, file, file.name)
  })
  const [accessToken] = getActiveTokens()

  return fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    method,
    body: formData,
  })
}

const settledFilesHandler = (result: PromiseSettledResult<Response>, fileNames: string[]) => {
  if (result.status === 'rejected') {
    return processLoadFileError()
  }
  if (result.status === 'fulfilled' && !result.value.ok) {
    return processLoadFileError(result.value.status, fileNames)
  }

  return result.value.json()
}

export const saveFile = async ({ formId, formCode, fieldName, models, source }: ISaveFileData) => {
  const preparedModels = models.map((model) => {
    const newModel: IReceiveModels = { ...model }
    delete newModel.file

    return { ...newModel, [fieldName]: model.file.name }
  })
  const url = proxy(`/form/data-set-provider/v4/${formId}/${formCode}/kendo/data`)

  const newModels = preparedModels.filter((model) => !model.UID)
  const updatedModels = preparedModels.filter((model) => !!model.UID)
  const filesData: IShortFilesUploadData[] = [
    { preparedModels: newModels, method: 'POST' },
    { preparedModels: updatedModels, method: 'PUT' },
  ]
  const promises = filesData.map(({ preparedModels, method }) => {
    return filesUploadHandler({
      preparedModels,
      models,
      url,
      fieldName,
      method,
    })
  })

  const [newModelsResult, updatedModelsResult] = await Promise.allSettled(promises)
  const successResults: Promise<ISaveResponse[]>[] = []
  const settledResultsData = [
    { currentModels: newModels, result: newModelsResult },
    { currentModels: updatedModels, result: updatedModelsResult },
  ]
  settledResultsData.forEach(({ currentModels, result }) => {
    const filesNames = currentModels.map((model) => model[fieldName])
    const settledResult = settledFilesHandler(result, filesNames as string[])
    if (typeof settledResult !== 'string') {
      successResults.push(settledResult)
    }
  })

  if (!successResults.length) {
    return ''
  }

  const response = await Promise.all(successResults)
  const responseModels = response.flat()
  if (source) {
    source.updateItem({ models: responseModels })
  }

  return responseModels.map(({ EntryId, [fieldName]: fileName, UID }) =>
    calculateFileLink({ formId, entryId: EntryId, fieldName, fileName: `${fileName}`, UID })
  )
}

export const calculateFileLink = (fileData: IBaseFileData) => {
  if (fileData.UID) {
    const extensionIndex = fileData.fileName?.lastIndexOf('.')
    const extension = fileData.fileName?.slice(extensionIndex + 1) || ''

    return proxy(
      `/form/${fileData.formId}/links/v2/${fileData.UID}/${fileData.fieldName}/.${extension}`
    )
  }

  return proxy(
    `/form/${fileData.formId}/links/${fileData.entryId}/${fileData.fieldName}/${fileData.fileName}`
  )
}

export const processLoadFileError = (errorCode?: number, fileName: string | string[] = '') => {
  const namesArray = typeof fileName === 'string' ? [fileName] : fileName

  const baseMessage = `Something went wrong with the file upload (${namesArray.join(', ')})`
  let errorMessage = `${baseMessage}. Try again.`
  switch (errorCode) {
    case 422:
      errorMessage = `${baseMessage}. Check the file extension or name and try again later.
      Allowed only latin characters, numbers, underscore and space. Dot is allowed as extension separator`
      break
  }

  NotificationController.showError({ message: errorMessage })
  return ''
}

export const saveFilesAdapter: IUploadAdapter = async ({
  file,
  needCloudinary,
  imageHeight,
  imageWidth,
}): Promise<string> => {
  const urls = await saveFile({
    formId: ATTACHMENT_FORM_ID,
    fieldName: FILE_FIELD_NAME,
    formCode: ATTACHMENT_FORM_CODE,
    models: [{ file }],
  })
  if (!urls || !urls.length) {
    return ''
  }
  const fileUrl = urls[0]
  if (needCloudinary) {
    const cloudinaryParams = {
      src: fileUrl,
      width: imageWidth,
      height: imageHeight,
    }
    return getCloudinaryUrl(cloudinaryParams)
  }
  return fileUrl
}
